import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Sparkles, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICING = [
  { tier: "Shared Bath Rooms", price: "From $1,190", per: "/month", note: "Comfortable furnished private rooms with a shared bathroom." },
  { tier: "Private Bath / Master Rooms", price: "From $1,450", per: "/month", note: "Furnished private rooms with a private or master bathroom." },
  { tier: "Premium Ensuite Rooms", price: "From $1,600", per: "/month", note: "Spacious ensuite rooms with maximum privacy and comfort." },
  { tier: "Private Studio Loft", price: "$1,980", per: "/month", note: "An entire private second-floor loft with its own bedroom, bath, kitchenette, and living area.", premium: true },
];

const EMPTY = {
  name: "", email: "", phone: "",
  check_in_date: "", check_out_date: "", length_of_stay: "", num_guests: "",
  preferred_accommodation: "", preferred_location: "", bathroom_preference: "",
  budget_range: "", reason_for_stay: "", referral_source: "",
  message: "",
};

const inputCls =
  "rounded-lg bg-white border-[#E7E5E4] focus-visible:ring-1 focus-visible:ring-[#8A9A86] focus-visible:border-[#8A9A86] h-12";
const selectCls = `${inputCls} data-[placeholder]:text-[#78716C]`;

export default function LongTermStays() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.check_in_date || !form.length_of_stay) {
      toast.error("Please fill in your name, email, phone, check-in date, and length of stay.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        inquiry_type: "longterm",
        message: form.message || "Long-term stay inquiry.",
      });
      setSubmitted(true);
      setForm(EMPTY);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="longterm-page" className="pt-28 md:pt-32">
      {/* Header */}
      <section className="px-6 md:px-12 pb-12">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-4xl">
            <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">
              Extended stays
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-[1.02] text-[#1C1917]">
              Long-Term Stays — 30+ Nights
            </h1>
            <p className="mt-8 text-lg text-[#44403C] leading-relaxed font-light max-w-2xl">
              Looking for a furnished stay for a month or longer? Tell us your dates and preferences,
              and our team will help you find the best available Masaya Hospitality option.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Pricing */}
      <section data-testid="longterm-pricing" className="px-6 md:px-12 py-16 md:py-24 bg-[#F5F5F4] border-y border-[#E7E5E4]">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-12 max-w-2xl">
            <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Monthly pricing</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#1C1917]">Starting monthly rates</h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PRICING.map((p, i) => (
              <Reveal key={p.tier} delay={(i % 4) * 0.05} y={16}>
                <div
                  data-testid={`price-tier-${i}`}
                  className={`h-full rounded-2xl p-8 border transition-colors duration-300 ${
                    p.premium
                      ? "bg-[#1C1917] text-white border-[#1C1917]"
                      : "bg-white border-[#E7E5E4]"
                  }`}
                >
                  {p.premium && (
                    <span className="inline-flex items-center gap-1.5 text-[0.65rem] tracking-[0.2em] uppercase font-medium text-[#8A9A86] mb-4">
                      <Sparkles className="w-3.5 h-3.5" /> Premium
                    </span>
                  )}
                  <p className={`text-xs tracking-[0.15em] uppercase mb-4 ${p.premium ? "text-[#A8A29E]" : "text-[#78716C]"}`}>{p.tier}</p>
                  <p className="text-4xl font-light tracking-tighter">
                    {p.price}
                    <span className={`text-base font-light ml-1 ${p.premium ? "text-[#A8A29E]" : "text-[#78716C]"}`}>{p.per}</span>
                  </p>
                  <p className={`mt-4 text-sm font-light leading-relaxed ${p.premium ? "text-[#D6D3D1]" : "text-[#44403C]"}`}>{p.note}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <p className="mt-8 text-sm text-[#78716C] leading-relaxed max-w-2xl">
              Rates vary by property, availability, and length of stay. Submit your dates to receive
              current availability and pricing.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Form */}
      <section className="px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-4xl mx-auto">
          {submitted ? (
            <Reveal>
              <div data-testid="longterm-confirmation" className="rounded-2xl bg-[#E8ECE7] border border-[#8A9A86] p-10 text-center">
                <div className="w-14 h-14 rounded-full bg-[#8A9A86] mx-auto flex items-center justify-center">
                  <Check className="w-6 h-6 text-white" strokeWidth={2} />
                </div>
                <h2 className="mt-6 text-3xl sm:text-4xl tracking-tight font-light text-[#1C1917]">Thank you for your inquiry!</h2>
                <p className="mt-4 text-lg text-[#44403C] leading-relaxed font-light">
                  Our Masaya Hospitality Reservation team will review your stay preferences and
                  contact you with available options.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  data-testid="longterm-new-inquiry"
                  className="mt-8 rounded-full bg-[#1C1917] text-white px-8 py-3.5 text-sm font-medium hover:bg-[#44403C] transition-colors duration-300"
                >
                  Submit another inquiry
                </button>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={handleSubmit} data-testid="longterm-form" className="rounded-2xl border border-[#E7E5E4] bg-[#F5F5F4] p-8 md:p-10 space-y-8">
                <div>
                  <h2 className="text-2xl font-normal tracking-tight text-[#1C1917]">Request availability</h2>
                  <p className="mt-3 text-[#44403C] font-light">Share a few details and our team will follow up with matching options.</p>
                </div>

                {/* Guest Information */}
                <div className="space-y-6">
                  <p className="text-xs tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Guest information</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="lt-name" className="text-[#1C1917]">Full Name</Label>
                      <Input id="lt-name" data-testid="lt-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your full name" className={inputCls} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lt-email" className="text-[#1C1917]">Email Address</Label>
                      <Input id="lt-email" type="email" data-testid="lt-email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" className={inputCls} />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="lt-phone" className="text-[#1C1917]">Phone Number</Label>
                      <Input id="lt-phone" data-testid="lt-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(512) 555-0123" className={inputCls} />
                    </div>
                  </div>
                </div>

                {/* Stay Information */}
                <div className="space-y-6">
                  <p className="text-xs tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Stay information</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="lt-checkin" className="text-[#1C1917]">Check-in Date</Label>
                      <Input id="lt-checkin" type="date" data-testid="lt-checkin" value={form.check_in_date} onChange={(e) => update("check_in_date", e.target.value)} className={inputCls} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lt-checkout" className="text-[#1C1917]">Preferred Move-out Date <span className="text-[#A8A29E]">(optional)</span></Label>
                      <Input id="lt-checkout" type="date" data-testid="lt-checkout" value={form.check_out_date} onChange={(e) => update("check_out_date", e.target.value)} className={inputCls} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#1C1917]">Expected Length of Stay</Label>
                      <Select value={form.length_of_stay} onValueChange={(v) => update("length_of_stay", v)}>
                        <SelectTrigger data-testid="lt-length" className={selectCls}><SelectValue placeholder="Select length" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="30–59 nights">30–59 nights</SelectItem>
                          <SelectItem value="60–89 nights">60–89 nights</SelectItem>
                          <SelectItem value="3–6 months">3–6 months</SelectItem>
                          <SelectItem value="6+ months">6+ months</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lt-guests" className="text-[#1C1917]">Number of Guests</Label>
                      <Input id="lt-guests" type="number" min="1" data-testid="lt-guests" value={form.num_guests} onChange={(e) => update("num_guests", e.target.value)} placeholder="e.g. 1" className={inputCls} />
                    </div>
                  </div>
                </div>

                {/* Accommodation Preference */}
                <div className="space-y-6">
                  <p className="text-xs tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Accommodation preference</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-[#1C1917]">Preferred Accommodation</Label>
                      <Select value={form.preferred_accommodation} onValueChange={(v) => update("preferred_accommodation", v)}>
                        <SelectTrigger data-testid="lt-accommodation" className={selectCls}><SelectValue placeholder="Select one" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Shared Bath Room">Shared Bath Room</SelectItem>
                          <SelectItem value="Private Bath / Master Room">Private Bath / Master Room</SelectItem>
                          <SelectItem value="Premium Ensuite Room">Premium Ensuite Room</SelectItem>
                          <SelectItem value="Private Studio Loft">Private Studio Loft</SelectItem>
                          <SelectItem value="No Preference">No Preference — Show me the best available option</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#1C1917]">Preferred Property Location <span className="text-[#A8A29E]">(optional)</span></Label>
                      <Select value={form.preferred_location} onValueChange={(v) => update("preferred_location", v)}>
                        <SelectTrigger data-testid="lt-location" className={selectCls}><SelectValue placeholder="Select area" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Northeast Austin, Texas">Northeast Austin, Texas</SelectItem>
                          <SelectItem value="Pflugerville, Texas">Pflugerville, Texas</SelectItem>
                          <SelectItem value="Tech Ridge Area">Tech Ridge Area</SelectItem>
                          <SelectItem value="Round Rock, Texas">Round Rock, Texas</SelectItem>
                          <SelectItem value="No Preference">No Preference</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#1C1917]">Bathroom Preference</Label>
                      <Select value={form.bathroom_preference} onValueChange={(v) => update("bathroom_preference", v)}>
                        <SelectTrigger data-testid="lt-bathroom" className={selectCls}><SelectValue placeholder="Select one" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Private bathroom">Private bathroom</SelectItem>
                          <SelectItem value="Shared bathroom">Shared bathroom</SelectItem>
                          <SelectItem value="No preference">No preference</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#1C1917]">Budget Range</Label>
                      <Select value={form.budget_range} onValueChange={(v) => update("budget_range", v)}>
                        <SelectTrigger data-testid="lt-budget" className={selectCls}><SelectValue placeholder="Select budget" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Under $1,200/month">Under $1,200/month</SelectItem>
                          <SelectItem value="$1,200–$1,499/month">$1,200–$1,499/month</SelectItem>
                          <SelectItem value="$1,500–$1,799/month">$1,500–$1,799/month</SelectItem>
                          <SelectItem value="$1,800–$2,099/month">$1,800–$2,099/month</SelectItem>
                          <SelectItem value="$2,100+/month">$2,100+/month</SelectItem>
                          <SelectItem value="Flexible">Flexible</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Reason & Referral */}
                <div className="space-y-6">
                  <p className="text-xs tracking-[0.2em] uppercase font-medium text-[#8A9A86]">A little more context</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-[#1C1917]">Reason for Stay</Label>
                      <Select value={form.reason_for_stay} onValueChange={(v) => update("reason_for_stay", v)}>
                        <SelectTrigger data-testid="lt-reason" className={selectCls}><SelectValue placeholder="Select one" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Employment / Work Assignment">Employment / Work Assignment</SelectItem>
                          <SelectItem value="Internship">Internship</SelectItem>
                          <SelectItem value="School / University">School / University</SelectItem>
                          <SelectItem value="Relocation">Relocation</SelectItem>
                          <SelectItem value="Healthcare / Travel Assignment">Healthcare / Travel Assignment</SelectItem>
                          <SelectItem value="Extended Visit">Extended Visit</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#1C1917]">How Did You Hear About Us?</Label>
                      <Select value={form.referral_source} onValueChange={(v) => update("referral_source", v)}>
                        <SelectTrigger data-testid="lt-referral" className={selectCls}><SelectValue placeholder="Select one" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Google">Google</SelectItem>
                          <SelectItem value="Referral">Referral</SelectItem>
                          <SelectItem value="Social Media">Social Media</SelectItem>
                          <SelectItem value="Booking Platform">Booking Platform</SelectItem>
                          <SelectItem value="Company / Employer">Company / Employer</SelectItem>
                          <SelectItem value="University / School">University / School</SelectItem>
                          <SelectItem value="Returning Guest">Returning Guest</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lt-message" className="text-[#1C1917]">Additional Message or Special Requests</Label>
                    <Textarea id="lt-message" data-testid="lt-message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Anything else we should know?" rows={4} className="rounded-lg bg-white border-[#E7E5E4] focus-visible:ring-1 focus-visible:ring-[#8A9A86] focus-visible:border-[#8A9A86] resize-none" />
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="lt-submit"
                  disabled={submitting}
                  className="w-full rounded-full bg-[#1C1917] text-white px-8 py-4 hover:bg-[#44403C] transition-colors duration-300 font-medium text-sm disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                  {submitting ? "Sending..." : (<>Request Availability <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} /></>)}
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
